<template>
  <div class="subscription-tab-wrapper">
    <div class="subscription-header mb-8">
      <h1 class="font-weight-medium">{{ $t('global.subscription.mySubscriptions') }}</h1>
    </div>

    <div class="subscription-tab-content">
      <div
        v-if="loadingSubscriptionData"
        class="d-flex justify-center mt-8 items-center align-center subscription-card"
      >
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </div>
      <div v-else class="subscription-card">
        <div v-if="isSubscriptionNotDefined">
          <h5>{{ $t('global.subscription.noSubscriptionRequired') }}</h5>
          <a class="subscription-service-link" :href="localizedLink" target="_blank">{{
            $t('global.moreInfoAboutSubscriptionServices')
          }}</a>
        </div>

        <div v-else>
          <!-- Icon -->
          <div class="subscription-icon">
            <v-icon size="32" color="primary">mdi-antenna</v-icon>
          </div>

          <h5>{{ $t('global.subscription.mobileConnectionM2M') }}</h5>
          <!-- Status Badge -->
          <div
            class="status-badge"
            :class="{
              expired: isSubscriptionExpired,
              'expiring-soon':
                !isMoreThan30DaysLater(subscriptionData?.expirationDate) && !isSubscriptionExpired,
              active:
                !isSubscriptionExpired && isMoreThan30DaysLater(subscriptionData?.expirationDate),
            }"
          >
            {{
              isSubscriptionExpired
                ? $t('global.subscription.subscriptionExpired', { days: daysUntilExpiration })
                : !isMoreThan30DaysLater(subscriptionData?.expirationDate)
                ? $t('global.subscription.subscriptionWillEndSoon', { days: daysUntilExpiration })
                : $t('global.subscription.activeSubscription')
            }}
          </div>

          <div class="subscription-dates">
            <div class="date-item">
              <span>{{ $t('global.subscription.subscriptionFrom') }}</span>
              <p>{{ formattedStartDate }}</p>
            </div>
            <div class="date-item">
              <span>{{ $t('global.subscription.subscriptionExpires') }}</span>
              <p>{{ formattedExpirationDate }}</p>
            </div>
          </div>

          <div class="subscription-info-actions">
            <a class="subscription-service-link" :href="localizedLink" target="_blank">{{
              $t('global.moreInfoAboutSubscriptionServices')
            }}</a>
            <button
              :disabled="isMoreThan30DaysLater(subscriptionData?.expirationDate)"
              class="v-btn primary v-size--default renew-button"
              @click="openDialog"
            >
              {{ $t('global.subscription.renew') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Billing Information Display -->
      <div class="billing-info-card">
        <div class="billing-header">
          <h2 class="billing-title">{{ $t('global.billing.billingInformation') }}</h2>
          <v-btn v-if="!editMode" icon small @click="enableEditMode">
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
        </div>

        <div v-if="loadingBillingInfo" class="d-flex justify-center mt-8">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </div>
        <div v-else>
          <!-- View Mode -->
          <div v-if="!editMode && billingInfo" class="billing-details">
            <div class="billing-row">
              <div class="billing-field">
                <span class="label">{{ $t('global.billing.firstName') }}</span>
                <span class="value">{{ billingInfo.firstName }}</span>
              </div>
              <div class="billing-field">
                <span class="label">{{ $t('global.billing.lastName') }}</span>
                <span class="value">{{ billingInfo.lastName ?? '-' }}</span>
              </div>
              <div class="billing-field">
                <span class="label">{{ $t('global.billing.company') }}</span>
                <span class="value">{{ billingInfo.company ?? '-' }}</span>
              </div>
            </div>
            <div class="billing-row">
              <div class="billing-field">
                <span class="label">{{ $t('global.billing.email') }}</span>
                <span class="value">{{ billingInfo.email ?? '-' }}</span>
              </div>
              <div class="billing-field">
                <span class="label">{{ $t('global.billing.phone') }}</span>
                <span class="value"
                  >{{ billingInfo.phonePrefix }} {{ billingInfo.phoneNumber ?? '-' }}</span
                >
              </div>
              <div class="billing-field">
                <span class="label">{{ $t('global.billing.country') }}</span>
                <span class="value">{{ displayCountry }}</span>
              </div>
            </div>
            <div class="billing-row">
              <div class="billing-field">
                <span class="label">{{ $t('global.billing.street') }}</span>
                <span class="value">{{ billingInfo.street ?? '-' }}</span>
              </div>
              <div class="billing-field">
                <span class="label">{{ $t('global.billing.streetNumber') }}</span>
                <span class="value">{{ billingInfo.streetNumber ?? '-' }}</span>
              </div>
              <div class="billing-field">
                <span class="label">{{ $t('global.billing.apartmentNumber') }}</span>
                <span class="value">{{ billingInfo.apartmentNumber ?? '-' }}</span>
              </div>
            </div>
            <div class="billing-row">
              <div class="billing-field">
                <span class="label">{{ $t('global.billing.zipCode') }}</span>
                <span class="value">{{ billingInfo.zipCode ?? '-' }}</span>
              </div>
              <div class="billing-field">
                <span class="label">{{ $t('global.billing.city') }}</span>
                <span class="value">{{ billingInfo.city ?? '-' }}</span>
              </div>
            </div>
            <div class="billing-row">
              <div class="billing-field">
                <span class="label">{{ $t('global.billing.vat') }}</span>
                <span class="value">{{ billingInfo.vat ?? '-' }}</span>
              </div>
              <div class="billing-field">
                <span class="label">{{ $t('global.billing.companyId') }}</span>
                <span class="value">{{ billingInfo.companyId ?? '-' }}</span>
              </div>
            </div>
          </div>

          <!-- Edit Mode -->
          <div v-if="editMode" class="billing-form">
            <v-form ref="billingForm" class="billing-edit-form" v-model="isFormValid">
              <div class="billing-form-content">
                <v-row>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="editedBilling.firstName"
                      :label="$t('global.billing.firstName')"
                      :rules="[rules.required]"
                      dense
                      :error-messages="
                        showErrors && !editedBilling.firstName
                          ? [$t('global.formValidation.required')]
                          : []
                      "
                      @focus="showAllErrors"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="editedBilling.lastName"
                      :label="$t('global.billing.lastName')"
                      :rules="[rules.required]"
                      dense
                      :error-messages="
                        showErrors && !editedBilling.lastName
                          ? [$t('global.formValidation.required')]
                          : []
                      "
                      @focus="showAllErrors"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="editedBilling.company"
                      :label="$t('global.billing.company')"
                      dense
                      @focus="showAllErrors"
                    ></v-text-field>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="editedBilling.email"
                      :label="$t('global.billing.email')"
                      :rules="[rules.email, rules.required]"
                      dense
                      :error-messages="getEmailErrors"
                      @focus="showAllErrors"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="6" md="2">
                    <v-select
                      v-model="editedBilling.phonePrefix"
                      :items="phonePrefixes"
                      :label="$t('global.billing.phonePrefix')"
                      dense
                      @focus="showAllErrors"
                    ></v-select>
                  </v-col>
                  <v-col cols="6" md="2">
                    <v-text-field
                      v-model="editedBilling.phoneNumber"
                      :label="$t('global.billing.phone')"
                      :rules="[rules.phone]"
                      dense
                      :error-messages="getPhoneErrors"
                      @focus="showAllErrors"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-select
                      v-model="editedBilling.country"
                      :items="countryItems"
                      item-text="text"
                      item-value="value"
                      :label="$t('global.billing.country')"
                      :rules="[rules.required]"
                      dense
                      :error-messages="
                        showErrors && !editedBilling.country
                          ? [$t('global.formValidation.required')]
                          : []
                      "
                      @focus="showAllErrors"
                    ></v-select>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="editedBilling.street"
                      :label="$t('global.billing.street')"
                      :rules="[rules.required]"
                      dense
                      :error-messages="
                        showErrors && !editedBilling.street
                          ? [$t('global.formValidation.required')]
                          : []
                      "
                      @focus="showAllErrors"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="6" md="2">
                    <v-text-field
                      v-model="editedBilling.streetNumber"
                      :label="$t('global.billing.streetNumber')"
                      :rules="[rules.numeric]"
                      dense
                      :error-messages="getNumericError('streetNumber')"
                      @focus="showAllErrors"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="6" md="2">
                    <v-text-field
                      v-model="editedBilling.apartmentNumber"
                      :label="$t('global.billing.apartmentNumber')"
                      :rules="[rules.numeric]"
                      dense
                      :error-messages="getNumericError('apartmentNumber')"
                      @focus="showAllErrors"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="6" md="2">
                    <v-text-field
                      v-model="editedBilling.zipCode"
                      :label="$t('global.billing.zipCode')"
                      :rules="[rules.required, rules.numeric]"
                      dense
                      :error-messages="getZipCodeErrors"
                      @focus="showAllErrors"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="6" md="2">
                    <v-text-field
                      v-model="editedBilling.city"
                      :label="$t('global.billing.city')"
                      :rules="[rules.required]"
                      dense
                      :error-messages="
                        showErrors && !editedBilling.city
                          ? [$t('global.formValidation.required')]
                          : []
                      "
                      @focus="showAllErrors"
                    ></v-text-field>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="6">
                    <v-text-field
                      v-model="editedBilling.vat"
                      :label="$t('global.billing.vat')"
                      dense
                      @focus="showAllErrors"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="6">
                    <v-text-field
                      v-model="editedBilling.companyId"
                      :label="$t('global.billing.companyId')"
                      dense
                      @focus="showAllErrors"
                    ></v-text-field>
                  </v-col>
                </v-row>
              </div>

              <div class="billing-form-actions">
                <v-btn
                  text
                  color="grey"
                  class="mr-2"
                  @click="cancelEdit"
                  :disabled="updatingBilling"
                >
                  {{ $t('global.cancel') }}
                </v-btn>
                <v-btn
                  color="primary"
                  @click="saveBillingInfo"
                  :loading="updatingBilling"
                  :disabled="updatingBilling || !isFormValid"
                >
                  {{ $t('global.save') }}
                </v-btn>
              </div>
            </v-form>
          </div>
        </div>

        <v-alert type="error" v-if="error">
          {{ error.message }}
        </v-alert>
      </div>
    </div>
    <SubscriptionRenewDialog :value="dialogOpen" :onClose="closeDialog" :device="device" />
  </div>
</template>

<script>
import SubscriptionRenewDialog from '../../Subscription/SubscriptionRenewDialog.vue'
import { subscriptionStatus } from '@/graphql/query/subscriptionStatus'
import { deviceBillingInfo } from '@/graphql/query/deviceBillingInfo'
import { billingInfoUpdate } from '@/graphql/mutations/billingInfoUpdate'
import { countryCodesWithName } from '../../../config/countryCodes'

export default {
  name: 'SubscriptionOverviewTab',
  components: {
    SubscriptionRenewDialog,
  },
  props: {
    tabConfig: Object,
    device: {
      type: Object,
      required: true,
    },
    deviceDetailWrapper: HTMLDivElement,
    active: Boolean,
  },
  data() {
    return {
      dialogOpen: false,
      loadingBillingInfo: true,
      loadingSubscriptionData: true,
      billingInfo: null,
      error: null,
      editMode: false,
      editedBilling: {
        firstName: '',
        lastName: '',
        company: '',
        email: '',
        phonePrefix: '',
        phoneNumber: '',
        country: '',
        street: '',
        streetNumber: '',
        apartmentNumber: '',
        zipCode: '',
        city: '',
        vat: '',
        companyId: '',
      },
      rules: {
        required: (value) => !!value || this.$t('global.formValidation.required'),
        email: (value) => {
          if (!value) return true // Allow empty email
          const pattern =
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
          return pattern.test(value) || this.$t('global.formValidation.notEmail')
        },
        phone: (value) => {
          if (!value) return true // Allow empty phone
          const pattern = /^\d{9,15}$/
          return pattern.test(value) || this.$t('global.formValidation.notPhoneNumber')
        },
        numeric: (value) => {
          if (!value) return true // Allow empty numeric fields
          const pattern = /^\d+$/
          return pattern.test(value) || this.$t('global.formValidation.notNumeric')
        },
      },
      phonePrefixes: ['+420', '+421', '+48', '+1', '+44', '+49', '+33', '+39', '+34', '+43'],
      countryItems: Object.entries(countryCodesWithName).map(([code, name]) => ({
        text: name,
        value: code,
      })),
      updatingBilling: false,
      isFormValid: false,
      showErrors: false,
      formErrors: {
        firstName: [],
        lastName: [],
        email: [],
        phoneNumber: [],
        country: [],
        street: [],
        streetNumber: [],
        apartmentNumber: [],
        zipCode: [],
        city: [],
      },
    }
  },
  apollo: {
    subscriptionData: {
      query: subscriptionStatus,
      variables() {
        return {
          deviceId: this.device.id,
        }
      },
      update(data) {
        this.loadingSubscriptionData = false
        return {
          subscriptionStatus: data.subscriptionStatus.subscriptionStatus.toLowerCase(),
          expirationDate: data.subscriptionStatus.expirationDate,
          startDate: data.subscriptionStatus.startDate,
        }
      },
      error(error) {
        this.loadingSubscriptionData = false
        console.error('Error fetching subscription status:', error)
        this.$toast.error(this.$t('global.errorFetchingData'))
      },
      loadingKey: 'loading',
    },
    billingInfo: {
      query: deviceBillingInfo,
      variables() {
        return {
          deviceId: this.device.id,
        }
      },
      loadingKey: 'loading',
      update(data) {
        this.loadingBillingInfo = false
        if (data?.billingInfo) {
          this.billingInfo = data.billingInfo
        }
        return data?.billingInfo
      },
      error(error) {
        this.loadingBillingInfo = false
        this.error = error
      },
    },
  },
  computed: {
    isSubscriptionValid() {
      return this.subscriptionData?.subscriptionStatus === 'valid'
    },
    isSubscriptionExpired() {
      return this.subscriptionData?.subscriptionStatus === 'expired'
    },
    isSubscriptionNotDefined() {
      return this.subscriptionData?.subscriptionStatus === 'not_defined'
    },
    formattedStartDate() {
      return this.subscriptionData?.startDate
        ? this.formatDate(this.subscriptionData.startDate)
        : '-'
    },
    formattedExpirationDate() {
      return this.subscriptionData?.expirationDate
        ? this.formatDate(this.subscriptionData.expirationDate)
        : '-'
    },
    localizedLink() {
      const lang = this.$i18n.locale
      console.log(lang)
      return `/business-info`
    },
    displayCountry() {
      if (!this.billingInfo?.country) return '-'
      return countryCodesWithName[this.billingInfo.country] || this.billingInfo.country
    },
    getEmailErrors() {
      if (!this.showErrors) return []
      const errors = []
      if (!this.editedBilling.email) {
        errors.push(this.$t('global.formValidation.required'))
      } else if (!this.rules.email(this.editedBilling.email)) {
        errors.push(this.$t('global.formValidation.notEmail'))
      }
      return errors
    },
    getPhoneErrors() {
      if (!this.showErrors || !this.editedBilling.phoneNumber) return []
      if (!this.rules.phone(this.editedBilling.phoneNumber)) {
        return [this.$t('global.formValidation.notPhoneNumber')]
      }
      return []
    },
    getZipCodeErrors() {
      if (!this.showErrors) return []
      const errors = []
      if (!this.editedBilling.zipCode) {
        errors.push(this.$t('global.formValidation.required'))
      } else if (!this.rules.numeric(this.editedBilling.zipCode)) {
        errors.push(this.$t('global.formValidation.notNumeric'))
      }
      return errors
    },
    daysUntilExpiration() {
      if (!this.subscriptionData?.expirationDate) return 0
      const now = new Date()
      const expirationDate = new Date(parseInt(this.subscriptionData.expirationDate))
      const diffTime = Math.abs(expirationDate - now)
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
      return diffDays
    },
  },
  methods: {
    closeDialog() {
      this.dialogOpen = false
    },
    openDialog() {
      this.dialogOpen = true
    },
    isMoreThan30DaysLater(timestamp) {
      const now = new Date()
      const futureDate = new Date()
      futureDate.setDate(now.getDate() + 30)
      return timestamp > futureDate.getTime()
    },
    formatDate(timestamp) {
      if (!timestamp) return '-'
      try {
        const date = new Date(parseInt(timestamp))
        if (isNaN(date.getTime())) return '-'
        return `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(
          2,
          '0',
        )}.${date.getFullYear()}`
      } catch (error) {
        console.error('Error formatting date:', error)
        return '-'
      }
    },
    showAllErrors() {
      this.showErrors = true
    },
    getNumericError(field) {
      if (!this.showErrors || !this.editedBilling[field]) return []
      return this.rules.numeric(this.editedBilling[field])
        ? []
        : [this.$t('global.formValidation.notNumeric')]
    },
    enableEditMode() {
      this.editMode = true
      this.editedBilling = { ...this.billingInfo }
      this.showErrors = false
    },
    cancelEdit() {
      this.editMode = false
      this.showErrors = false
    },
    async saveBillingInfo() {
      const isValid = await this.$refs.billingForm.validate()

      if (!isValid) {
        this.$toast.error(this.$t('global.fillRequiredFields'))
        return
      }

      this.updatingBilling = true

      try {
        // Format phone number in the expected format
        const phone =
          this.editedBilling.phoneNumber !== ''
            ? this.editedBilling.phonePrefix + ' ' + this.editedBilling.phoneNumber
            : ''

        // Prepare the input for the mutation
        const billingInfoInput = {
          firstName: this.editedBilling.firstName,
          lastName: this.editedBilling.lastName,
          company: this.editedBilling.company,
          phone: phone,
          country: this.editedBilling.country,
          street: this.editedBilling.street,
          streetNumber: this.editedBilling.streetNumber,
          apartmentNumber: this.editedBilling.apartmentNumber,
          zipCode: this.editedBilling.zipCode,
          city: this.editedBilling.city,
          email: this.editedBilling.email,
          vat: this.editedBilling.vat,
          companyId: this.editedBilling.companyId,
        }

        // Call the mutation
        const { data } = await this.$apollo.mutate({
          mutation: billingInfoUpdate,
          variables: {
            deviceId: this.device.id,
            billingInfo: billingInfoInput,
          },
        })

        if (data.billingInfoUpdate?.success) {
          this.$toast.success(this.$t('global.dataUpdateSuccess'))
          this.editMode = false

          // Refresh billing info data by refetching the query
          this.$apollo.queries.billingInfo.refetch()
        } else {
          this.$toast.error(this.$t('global.billing.updateError'))
        }
      } catch (error) {
        console.error('Error updating billing info:', error)
        this.$toast.error(error.message || this.$t('global.billing.updateError'))
      } finally {
        this.updatingBilling = false
      }
    },
  },
  watch: {
    editedBilling: {
      deep: true,
      handler() {
        if (this.showErrors) {
          this.validateAllFields()
        }
      },
    },
  },
}
</script>

<style lang="less" scoped>
@import '~@/assets/less/variables.less';

.subscription-header {
  border-radius: 4px;
  position: relative;

  h1 {
    margin: 0;
    padding: 0.5rem 0;
    color: #333;
    font-size: 14px;
  }
}

.subscription-tab-content {
  display: grid;
  grid-template-columns: 2fr 4fr;
  flex-direction: row;
  gap: 24px;

  @media (max-width: 1200px) {
    display: flex;
    flex-direction: column;
  }
}

.subscription-tab-wrapper {
  border: 1px solid #e5e5e5;
  padding: 8px 24px 32px 24px;
  h1 {
    font-size: 24px;
  }

  h2 {
    font-weight: 400;
    font-size: 14px;
    color: #8089ac;
  }
  h5 {
    font-size: 1rem;
    margin-bottom: 8px;
  }
  .subscription-info-actions {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .subscription-service-link {
    font-size: 14px;
    color: #8089ac;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
  p {
    font-size: 0.875rem;
    margin-bottom: 0;
  }

  .renew-button {
    width: 100%;
    margin-top: 16px;
  }

  .renew-button:disabled {
    background-color: #dfe4e9 !important;
    color: #b3bfcb;
  }

  .subscription-info {
    display: flex;
    width: fit-content;
    margin-top: 20px;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
    width: 100%;
    padding: 24px;
    height: 300px;
    background-color: rgba(211, 241, 255, 0.5);
  }

  .billing-info-card {
    grid-column-start: 1;
    grid-column-end: 6;
    flex: 1;
    background: white;
    border-radius: 0;
    padding: 24px;
    border: 1px solid #e5e5e5;
    min-width: 0;
    width: 100%;
    min-height: 372px;
    display: flex;
    flex-direction: column;
  }

  .billing-title {
    color: #333;
    font-size: 20px;
    font-weight: 500;
    margin-bottom: 24px;
  }

  .billing-details {
    display: flex;
    flex-direction: column;
    gap: 20px;
    flex: 1;
  }

  .billing-row {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 24px;

    @media (max-width: 768px) {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }
  }

  .billing-field {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .label {
    color: #8089ac;
    font-size: 14px;
  }

  .value {
    color: #333;
    font-size: 14px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .v-alert {
    margin-top: 16px;
  }

  .billing-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
  }

  .billing-form {
    margin-top: 16px;
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  .billing-form .v-form {
    display: flex;
    flex-direction: column;
    flex: 1;
  }

  .billing-form .v-row {
    margin-bottom: 16px;
  }

  .billing-form .v-col {
    padding-top: 8px;
    padding-bottom: 8px;
  }

  .billing-form .d-flex.justify-end {
    margin-top: auto;
    padding-top: 24px;
  }

  /* Ensure labels have consistent appearance with view mode */
  .v-text-field .v-label,
  .v-select .v-label {
    color: #8089ac;
    font-size: 14px;
  }

  /* Add a bit more height to the inputs */
  .v-input--dense .v-input__slot {
    min-height: 36px !important;
  }

  /* Ensure consistent spacing between form fields */
  .v-text-field.v-text-field--enclosed .v-input__slot,
  .v-select.v-select--enclosed .v-input__slot {
    margin-bottom: 4px;
  }

  .billing-edit-form {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-height: 300px; /* Match the height of view mode */
  }

  .billing-form-content {
    flex: 1;
  }

  .billing-form-actions {
    display: flex;
    justify-content: flex-end;
    padding-top: 24px;
    margin-top: auto;
  }
}

.subscription-card {
  flex: 0 0 300px;
  background: white;
  border-radius: 0;
  padding: 24px;
  text-align: center;
  border: 1px solid #e5e5e5;
  align-self: flex-start;
  min-height: 372px;
  width: 100%;
}

.subscription-icon {
  background: #f5f5f5;
  width: 64px;
  height: 64px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
}

.status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 16px;
  background: #27ae60; /* Green for active */
  color: white;
  font-size: 14px;
  margin-bottom: 16px;
}

.status-badge.expired {
  background: #ff4d4f; /* Red for expired */
}

.status-badge.expiring-soon {
  background: #ffe7e7; /* Light red background */
  color: #ff4d4f; /* Red text */
}

.subscription-dates {
  text-align: left;
  margin-bottom: 24px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.date-item {
  margin-bottom: 8px;
  display: flex;
  gap: 8px;
  align-items: center;
}

.date-item span {
  font-size: 14px;
  color: #8089ac;
}

.date-item p {
  font-size: 14px;
  color: #454d57;
}

.subscription-info-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.subscription-service-link {
  color: var(--v-primary-base);
  text-decoration: none;
}

.renew-button {
  width: 100%;
}
</style>
